import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

// Touring guide place images (uploaded)
import imgKarnak from '../assets-material/images/karnak.jpg';
import imgValleyOfTheKings from '../assets-material/images/vally of the kings.jpg.webp';
import imgAbuSimbel from '../assets-material/images/abuSimble.jpeg';
import imgPhilae from '../assets-material/images/philatemple.webp';
import imgEgyptianMuseum from '../assets-material/images/egyptian musuem.jpeg';
import imgSaintCatherine from '../assets-material/images/saint-catherine-s-monastery.jpg';
const TouringGuide = () => {
  const { t } = useTranslation();
  const { language } = useLanguageCurrency();
  const placesScrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const scrollIntervalRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set initial value
    setIsMobile(window.innerWidth < 768);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateScrollButtons = () => {
    const container = placesScrollRef.current;
    if (!container) return;
    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < maxScroll - 1);
  };

  const scrollLeft = () => {
    const container = placesScrollRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 350;
    container.scrollBy({ left: -cardWidth - 24, behavior: 'smooth' });
  };

  const scrollRight = () => {
    const container = placesScrollRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 350;
    container.scrollBy({ left: cardWidth + 24, behavior: 'smooth' });
  };

  const startScrollingLeft = () => {
    if (scrollIntervalRef.current) return;
    scrollIntervalRef.current = setInterval(() => {
      const container = placesScrollRef.current;
      if (!container) return;
      container.scrollBy({ left: -10, behavior: 'auto' });
    }, 16);
  };

  const startScrollingRight = () => {
    if (scrollIntervalRef.current) return;
    scrollIntervalRef.current = setInterval(() => {
      const container = placesScrollRef.current;
      if (!container) return;
      container.scrollBy({ left: 10, behavior: 'auto' });
    }, 16);
  };

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    const container = placesScrollRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      setTimeout(updateScrollButtons, 500);
    }
    return () => {
      if (container) container.removeEventListener('scroll', updateScrollButtons);
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
    };
  }, []);

  const amazingPlaces = [
    {
      id: 1,
      name: language === 'ar' ? 'أهرامات الجيزة' : 'Great Pyramid of Giza',
      nameEn: 'Great Pyramid of Giza',
      location: language === 'ar' ? 'الجيزة' : 'Giza',
      description: language === 'ar' 
        ? 'أحد عجائب الدنيا السبع القديمة، وأكبر هرم في مصر. بني منذ أكثر من 4500 عام وهو شاهد على عظمة الحضارة المصرية القديمة.'
        : 'One of the Seven Wonders of the Ancient World and the largest pyramid in Egypt. Built over 4,500 years ago, it stands as a testament to the greatness of ancient Egyptian civilization.',
      image: 'https://images.unsplash.com/photo-1539650116574-8efeb43e2750?w=1200&h=800&fit=crop&q=80&auto=format',
      highlights: language === 'ar' 
        ? ['أحد عجائب الدنيا السبع', 'أكثر من 4500 عام', 'معبد أبو الهول']
        : ['One of Seven Wonders', 'Over 4,500 years old', 'Sphinx Temple'],
      whenToGo: language === 'ar' 
        ? 'أكتوبر - أبريل (أفضل وقت)، تجنب الصيف الحار'
        : 'October - April (best time), avoid hot summer months',
      howToGo: language === 'ar' 
        ? 'من القاهرة: 30 دقيقة بالسيارة أو المترو. من المطار: 45 دقيقة بالسيارة.'
        : 'From Cairo: 30 minutes by car or metro. From airport: 45 minutes by car.',
      howMuch: language === 'ar' 
        ? 'تذكرة دخول: 200-400 جنيه مصري (حسب المنطقة)'
        : 'Entry ticket: 200-400 EGP (varies by area)'
    },
    {
      id: 2,
      name: language === 'ar' ? 'معبد الكرنك' : 'Karnak Temple',
      nameEn: 'Karnak Temple',
      location: language === 'ar' ? 'الأقصر' : 'Luxor',
      description: language === 'ar'
        ? 'أكبر مجمع معابد في العالم، يضم أكثر من 2000 عام من التاريخ المصري. أعمدة هائلة ومنحوتات رائعة تجسد عظمة الفراعنة.'
        : 'The largest temple complex in the world, spanning over 2,000 years of Egyptian history. Massive columns and stunning carvings showcase the grandeur of the pharaohs.',
      image: imgKarnak,
      highlights: language === 'ar'
        ? ['أكبر مجمع معابد', 'أعمدة ضخمة', 'تاريخ يمتد 2000 عام']
        : ['Largest temple complex', 'Massive columns', '2,000 years of history'],
      whenToGo: language === 'ar' 
        ? 'أكتوبر - مارس (طقس معتدل)، تجنب الصيف الحار جداً'
        : 'October - March (mild weather), avoid very hot summer',
      howToGo: language === 'ar' 
        ? 'من القاهرة: رحلة جوية 1.5 ساعة أو قطار 10 ساعات. داخل الأقصر: 15 دقيقة بالسيارة.'
        : 'From Cairo: 1.5 hour flight or 10 hour train. Within Luxor: 15 minutes by car.',
      howMuch: language === 'ar' 
        ? 'تذكرة دخول: 150-200 جنيه مصري'
        : 'Entry ticket: 150-200 EGP'
    },
    {
      id: 3,
      name: language === 'ar' ? 'وادي الملوك' : 'Valley of the Kings',
      nameEn: 'Valley of the Kings',
      location: language === 'ar' ? 'الأقصر' : 'Luxor',
      description: language === 'ar'
        ? 'مقبرة الملوك الفراعنة حيث دفن ملوك مصر القديمة. يحتوي على مقابر مذهلة مثل مقبرة توت عنخ آمون الشهيرة.'
        : 'The royal necropolis where ancient Egyptian pharaohs were buried. Home to stunning tombs including the famous tomb of Tutankhamun.',
      image: imgValleyOfTheKings,
      highlights: language === 'ar'
        ? ['مقابر الفراعنة', 'مقبرة توت عنخ آمون', 'رسومات جدارية رائعة']
        : ['Pharaoh tombs', 'Tutankhamun\'s tomb', 'Stunning wall paintings'],
      whenToGo: language === 'ar' 
        ? 'أكتوبر - مارس (طقس مثالي)، الصباح الباكر أفضل'
        : 'October - March (ideal weather), early morning is best',
      howToGo: language === 'ar' 
        ? 'من الأقصر: 30 دقيقة بالسيارة أو 20 دقيقة بالقارب عبر النيل'
        : 'From Luxor: 30 minutes by car or 20 minutes by boat across the Nile',
      howMuch: language === 'ar' 
        ? 'تذكرة دخول: 240-300 جنيه مصري (تتضمن 3 مقابر)'
        : 'Entry ticket: 240-300 EGP (includes 3 tombs)'
    },
    {
      id: 4,
      name: language === 'ar' ? 'أبو سمبل' : 'Abu Simbel',
      nameEn: 'Abu Simbel',
      location: language === 'ar' ? 'أسوان' : 'Aswan',
      description: language === 'ar'
        ? 'معبدان ضخمان منحوتان في الصخر، بناهما رمسيس الثاني. تم نقلهما بالكامل في الستينيات لإنقاذهما من فيضان بحيرة ناصر.'
        : 'Two massive rock-cut temples built by Ramesses II. They were completely relocated in the 1960s to save them from the flooding of Lake Nasser.',
      image: imgAbuSimbel,
      highlights: language === 'ar'
        ? ['منحوت في الصخر', 'تم نقله بالكامل', 'تمثال رمسيس الثاني الضخم']
        : ['Rock-cut temples', 'Completely relocated', 'Massive Ramesses II statue'],
      whenToGo: language === 'ar' 
        ? 'أكتوبر - أبريل (طقس لطيف)، شروق الشمس الساعة 6 صباحاً مذهل'
        : 'October - April (pleasant weather), sunrise at 6 AM is spectacular',
      howToGo: language === 'ar' 
        ? 'من أسوان: رحلة جوية 45 دقيقة أو رحلة برية 3.5 ساعة. من القاهرة: رحلة جوية مباشرة.'
        : 'From Aswan: 45 minute flight or 3.5 hour road trip. From Cairo: direct flight available.',
      howMuch: language === 'ar' 
        ? 'تذكرة دخول: 255 جنيه مصري'
        : 'Entry ticket: 255 EGP'
    },
    {
      id: 5,
      name: language === 'ar' ? 'شرم الشيخ' : 'Sharm El Sheikh',
      nameEn: 'Sharm El Sheikh',
      location: language === 'ar' ? 'شرم الشيخ' : 'Sharm El Sheikh',
      description: language === 'ar'
        ? 'مدينة ساحلية رائعة على البحر الأحمر، تشتهر بالغوص والأنشطة المائية. شواطئ خلابة وحيوانات بحرية متنوعة.'
        : 'A stunning coastal city on the Red Sea, famous for diving and water activities. Picturesque beaches and diverse marine life.',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1200&h=800&fit=crop&q=80&auto=format',
      highlights: language === 'ar'
        ? ['غوص عالمي', 'شواطئ رائعة', 'حياة بحرية متنوعة']
        : ['World-class diving', 'Stunning beaches', 'Diverse marine life'],
      whenToGo: language === 'ar' 
        ? 'على مدار السنة (طقس دافئ)، أفضل وقت: مارس - مايو وسبتمبر - نوفمبر'
        : 'Year-round (warm weather), best time: March - May and September - November',
      howToGo: language === 'ar' 
        ? 'من القاهرة: رحلة جوية 1.5 ساعة. من المطار: 15 دقيقة بالسيارة إلى المنتجعات.'
        : 'From Cairo: 1.5 hour flight. From airport: 15 minutes by car to resorts.',
      howMuch: language === 'ar' 
        ? 'الشواطئ مجانية، رحلات الغوص: 50-150 دولار حسب المدة'
        : 'Beaches are free, diving trips: $50-150 depending on duration'
    },
    {
      id: 6,
      name: language === 'ar' ? 'معبد فيلة' : 'Philae Temple',
      nameEn: 'Philae Temple',
      location: language === 'ar' ? 'أسوان' : 'Aswan',
      description: language === 'ar'
        ? 'معبد جميل مخصص للإلهة إيزيس، يقع على جزيرة فيلة. تم نقله بالكامل إلى جزيرة أجيليكا لإنقاذه من الفيضان.'
        : 'A beautiful temple dedicated to the goddess Isis, located on Philae Island. Completely relocated to Agilkia Island to save it from flooding.',
      image: imgPhilae,
      highlights: language === 'ar'
        ? ['مخصص لإيزيس', 'منحوتات رائعة', 'موقع جزيرة خلاب']
        : ['Dedicated to Isis', 'Beautiful carvings', 'Picturesque island location'],
      whenToGo: language === 'ar' 
        ? 'أكتوبر - أبريل (طقس معتدل)، تجنب حرارة الصيف'
        : 'October - April (mild weather), avoid summer heat',
      howToGo: language === 'ar' 
        ? 'من أسوان: 15 دقيقة بالسيارة إلى الميناء، ثم 10 دقائق بالقارب إلى الجزيرة'
        : 'From Aswan: 15 minutes by car to port, then 10 minutes by boat to island',
      howMuch: language === 'ar' 
        ? 'تذكرة دخول: 180 جنيه مصري + 100 جنيه للقارب'
        : 'Entry ticket: 180 EGP + 100 EGP for boat'
    },
    {
      id: 7,
      name: language === 'ar' ? 'دير سانت كاترين' : 'Saint Catherine\'s Monastery',
      nameEn: 'Saint Catherine\'s Monastery',
      location: language === 'ar' ? 'سيناء' : 'Sinai',
      description: language === 'ar'
        ? 'أقدم دير مسيحي في العالم، يقع عند سفح جبل موسى. يحتوي على مكتبة قديمة ومخطوطات نادرة.'
        : 'The oldest Christian monastery in the world, located at the foot of Mount Moses. Houses an ancient library and rare manuscripts.',
      image: imgSaintCatherine,
      highlights: language === 'ar'
        ? ['أقدم دير مسيحي', 'جبل موسى', 'مخطوطات نادرة']
        : ['Oldest Christian monastery', 'Mount Moses', 'Rare manuscripts'],
      whenToGo: language === 'ar' 
        ? 'أكتوبر - أبريل (طقس بارد لطيف)، تجنب الصيف الحار والشتاء البارد جداً'
        : 'October - April (pleasant cool weather), avoid hot summer and very cold winter',
      howToGo: language === 'ar' 
        ? 'من شرم الشيخ: 2.5 ساعة بالسيارة. من القاهرة: رحلة جوية إلى شرم ثم سيارة.'
        : 'From Sharm El Sheikh: 2.5 hours by car. From Cairo: flight to Sharm then car.',
      howMuch: language === 'ar' 
        ? 'الدخول مجاني، تبرعات مقبولة'
        : 'Entry is free, donations accepted'
    },
    {
      id: 8,
      name: language === 'ar' ? 'المتحف المصري' : 'Egyptian Museum',
      nameEn: 'Egyptian Museum',
      location: language === 'ar' ? 'القاهرة' : 'Cairo',
      description: language === 'ar'
        ? 'يحتوي على أكبر مجموعة من الآثار المصرية القديمة في العالم، بما في ذلك كنوز توت عنخ آمون الشهيرة.'
        : 'Houses the world\'s largest collection of ancient Egyptian antiquities, including the famous treasures of Tutankhamun.',
      image: imgEgyptianMuseum,
      highlights: language === 'ar'
        ? ['أكبر مجموعة آثار', 'كنوز توت عنخ آمون', 'أكثر من 120,000 قطعة']
        : ['Largest artifact collection', 'Tutankhamun treasures', 'Over 120,000 pieces'],
      whenToGo: language === 'ar' 
        ? 'على مدار السنة، أفضل وقت: أكتوبر - أبريل (طقس معتدل)'
        : 'Year-round, best time: October - April (mild weather)',
      howToGo: language === 'ar' 
        ? 'في وسط القاهرة، 10 دقائق سيراً من ميدان التحرير. مترو: محطة السادات.'
        : 'In downtown Cairo, 10 minutes walk from Tahrir Square. Metro: Sadat station.',
      howMuch: language === 'ar' 
        ? 'تذكرة دخول: 200 جنيه مصري (مصرية)، 300 جنيه (أجنبية)'
        : 'Entry ticket: 200 EGP (Egyptian), 300 EGP (foreign)'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section with Gradient Background */}
      <section 
        className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(180deg, rgba(29, 42, 59, 0.98) 0%, rgba(29, 42, 59, 0.96) 30%, rgba(29, 42, 59, 0.95) 60%, rgba(29, 42, 59, 0.95) 100%)',
          width: '100%',
          minHeight: 'auto',
          position: 'relative'
        }}
      >
        {/* Subtle Gold Glow at Top */}
        <div className="absolute top-0 left-0 right-0 h-1/3 opacity-30" style={{
          background: 'linear-gradient(180deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.05) 50%, transparent 100%)',
          pointerEvents: 'none'
        }}></div>
        
        {/* Subtle Decorative Elements - Reduced */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-3" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.2) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
          filter: 'blur(50px)'
        }}></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-3" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.15) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
          filter: 'blur(50px)'
        }}></div>

        <div className="w-full max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-1 mx-auto mb-3 rounded-full animate-line-expand" style={{ 
                background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
              }}></div>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight relative flex items-center justify-center flex-wrap gap-2 sm:gap-4" style={{ 
              textShadow: '0 0 30px rgba(213, 181, 119, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3)',
              fontSize: 'clamp(3.5rem, 7vw, 6rem)'
            }}>
              <span className="relative inline-flex items-center justify-center gap-2 sm:gap-4">
                {/* Touring Guide Icon */}
                <svg 
                  className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 animate-icon-float"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    color: '#D5B577',
                    filter: 'drop-shadow(0 0 10px rgba(213, 181, 119, 0.5))'
                  }}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" 
                  />
                </svg>
                {/* Animated Text */}
                <span className="touring-guide-text" style={{ fontSize: 'inherit' }}>
                  {language === 'ar' ? (
                    <span
                      style={{
                        background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                        backgroundSize: '200% 200%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'gradient-shift-once 3s ease-in-out forwards',
                        fontSize: 'inherit'
                      }}
                    >
                      {language === 'ar' ? 'دليل الجولات في مصر' : 'Touring Guide to Egypt'}
                    </span>
                  ) : (
                    (language === 'ar' ? 'دليل الجولات في مصر' : 'Touring Guide to Egypt').split('').map((char, index) => (
                      <span
                        key={index}
                        className="inline-block"
                        style={{
                          animationDelay: `${index * 0.08}s`,
                          background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                          backgroundSize: '200% 200%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          animation: 'gradient-shift-once 3s ease-in-out forwards',
                          fontSize: 'inherit'
                        }}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))
                  )}
                </span>
                {/* Animated underline */}
                <div className="absolute -bottom-2 left-0 right-0 h-0.5 animate-underline-expand" style={{
                  background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                  opacity: 0.8,
                  minWidth: '100%'
                }}></div>
                {/* Glow effect */}
                <div className="absolute inset-0 animate-pulse-glow" style={{
                  background: 'radial-gradient(circle, rgba(213, 181, 119, 0.2) 0%, transparent 70%)',
                  filter: 'blur(20px)',
                  zIndex: -1
                }}></div>
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed font-light animate-fade-in-up" style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              animationDelay: '0.8s',
              animationFillMode: 'both'
            }}>
              {language === 'ar' 
                ? 'اكتشف عجائب مصر القديمة والحديثة - من الأهرامات الشامخة إلى الشواطئ الخلابة'
                : 'Discover the wonders of ancient and modern Egypt - from majestic pyramids to stunning beaches'}
            </p>
            <div className="w-20 h-1 mx-auto mt-6 rounded-full animate-line-expand-delayed" style={{ 
              background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
              boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
            }}></div>
          </div>
        </div>
      </section>

      {/* Amazing Places Section - Alternating Layout */}
      <section className="pt-12 pb-12 sm:pb-16 md:pb-20 bg-slate-900 relative" style={{ 
        width: '100%', 
        minHeight: 'auto',
        marginTop: '-120px',
        zIndex: 10,
        paddingTop: '140px'
      }}>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              {language === 'ar' ? 'أروع الأماكن في مصر' : 'Most Amazing Places in Egypt'}
            </h2>
            <p className="text-base sm:text-lg text-amber-100 max-w-2xl mx-auto px-4">
              {language === 'ar'
                ? 'استكشف كنوز مصر التاريخية والطبيعية التي تجذب ملايين الزوار من جميع أنحاء العالم'
                : 'Explore Egypt\'s historical and natural treasures that attract millions of visitors from around the world'}
            </p>
          </div>

          {/* Places - Alternating Layout */}
          <div className="space-y-16 sm:space-y-20 md:space-y-24">
            {amazingPlaces.map((place, index) => {
              const isEven = index % 2 === 0;
              const isRTL = language === 'ar';
              const imageLeft = (isEven && !isRTL) || (!isEven && isRTL);
              
              return (
                <div
                  key={place.id}
                  className={`flex flex-col ${imageLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-12 transition-all duration-500 hover:scale-[1.01]`}
                >
                  {/* Image Section */}
                  <div 
                    className="w-full lg:w-1/2 relative rounded-2xl overflow-hidden shadow-2xl group"
                    style={{
                      aspectRatio: '16/10',
                      border: '1px solid rgba(213, 181, 119, 0.2)',
                      minHeight: '400px',
                      maxHeight: '600px',
                      backgroundColor: '#0F172A',
                      display: 'flex',
                      alignItems: 'stretch'
                    }}
                  >
                    <img
                      src={place.image}
                      alt={place.nameEn}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        minHeight: '400px'
                      }}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://picsum.photos/1200/800?random=${place.id}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-300">{place.location}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="w-full lg:w-1/2 flex flex-col justify-center">
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                          {place.name}
                        </h3>
                        <div className="w-16 h-1 rounded-full mb-6" style={{ 
                          background: 'linear-gradient(90deg, #D5B577, rgba(213, 181, 119, 0.5))'
                        }}></div>
                      </div>
                      
                      <p className="text-lg text-gray-300 leading-relaxed">
                        {place.description}
                      </p>

                      {/* Highlights */}
                      <div className="pt-4">
                        <p className="text-sm font-semibold mb-3 text-white" style={{ color: '#D5B577' }}>
                          {language === 'ar' ? 'أبرز المعالم:' : 'Highlights:'}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {place.highlights.map((highlight, idx) => (
                            <span
                              key={idx}
                              className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105"
                              style={{
                                backgroundColor: 'rgba(213, 181, 119, 0.1)',
                                color: '#D5B577',
                                border: '1px solid rgba(213, 181, 119, 0.3)'
                              }}
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Practical Information */}
                      <div className="pt-6 mt-6 border-t" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                        <div className="space-y-4">
                          {/* When to Go */}
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold mb-1" style={{ color: '#D5B577' }}>
                                {language === 'ar' ? 'أفضل وقت للزيارة:' : 'Best Time to Visit:'}
                              </p>
                              <p className="text-sm text-gray-300 leading-relaxed">
                                {place.whenToGo}
                              </p>
                            </div>
                          </div>

                          {/* How to Go */}
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold mb-1" style={{ color: '#D5B577' }}>
                                {language === 'ar' ? 'كيفية الوصول:' : 'How to Get There:'}
                              </p>
                              <p className="text-sm text-gray-300 leading-relaxed">
                                {place.howToGo}
                              </p>
                            </div>
                          </div>

                          {/* How Much */}
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold mb-1" style={{ color: '#D5B577' }}>
                                {language === 'ar' ? 'التكلفة:' : 'Cost:'}
                              </p>
                              <p className="text-sm text-gray-300 leading-relaxed">
                                {place.howMuch}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Travel Tips Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-2 text-white text-center mb-12">
            {language === 'ar' ? 'نصائح للسياح' : 'Travel Tips'}
          </h2>
          <div className="bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-xl border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
            <div className="space-y-6 text-gray-300">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-white" style={{ color: '#D5B577' }}>
                  {language === 'ar' ? 'أفضل وقت للزيارة' : 'Best Time to Visit'}
                </h3>
                <p className="leading-relaxed">
                  {language === 'ar'
                    ? 'أفضل وقت لزيارة مصر هو من أكتوبر إلى أبريل عندما يكون الطقس معتدلاً. تجنب أشهر الصيف الحارة.'
                    : 'The best time to visit Egypt is from October to April when the weather is mild. Avoid the hot summer months.'}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3 text-white" style={{ color: '#D5B577' }}>
                  {language === 'ar' ? 'ما يجب إحضاره' : 'What to Bring'}
                </h3>
                <p className="leading-relaxed">
                  {language === 'ar'
                    ? 'احضر ملابس قطنية خفيفة، واقي الشمس، قبعة، ماء، وكاميرا لالتقاط الذكريات الرائعة.'
                    : 'Bring light cotton clothing, sunscreen, a hat, water, and a camera to capture amazing memories.'}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3 text-white" style={{ color: '#D5B577' }}>
                  {language === 'ar' ? 'الاحترام الثقافي' : 'Cultural Respect'}
                </h3>
                <p className="leading-relaxed">
                  {language === 'ar'
                    ? 'احترم التقاليد المحلية، ارتدِ ملابس محتشمة عند زيارة الأماكن الدينية، واطلب الإذن قبل التقاط الصور.'
                    : 'Respect local traditions, dress modestly when visiting religious sites, and ask permission before taking photos.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Add CSS animations
if (typeof document !== 'undefined') {
  const styleId = 'touring-guide-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes gradient-shift {
        0%, 100% { 
          background-position: 0% 50%;
          transform: translateY(0);
        }
        50% { 
          background-position: 100% 50%;
          transform: translateY(-3px);
        }
      }
      
      @keyframes gradient-shift-once {
        0% { 
          background-position: 0% 50%;
          transform: translateY(0);
          opacity: 0;
        }
        50% { 
          background-position: 100% 50%;
          transform: translateY(-3px);
          opacity: 1;
        }
        100% { 
          background-position: 0% 50%;
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes underline-expand {
        0% { 
          width: 0;
          opacity: 0;
        }
        50% {
          opacity: 0.8;
        }
        100% { 
          width: 100%;
          opacity: 0.8;
        }
      }
      
      .animate-underline-expand {
        animation: underline-expand 1.2s ease-out 0.5s forwards;
        width: 100%;
        opacity: 0.8;
      }
      
      @keyframes pulse-glow {
        0%, 100% { 
          opacity: 0.3;
          transform: scale(1);
        }
        50% { 
          opacity: 0.6;
          transform: scale(1.1);
        }
      }
      
      @keyframes line-expand {
        0% { 
          width: 0;
          opacity: 0;
        }
        100% { 
          width: 80px;
          opacity: 1;
        }
      }
      
      @keyframes line-expand-delayed {
        0% { 
          width: 0;
          opacity: 0;
        }
        50% {
          opacity: 0;
        }
        100% { 
          width: 80px;
          opacity: 1;
        }
      }
      
      @keyframes fade-in-up {
        0% { 
          opacity: 0;
          transform: translateY(20px);
        }
        100% { 
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes icon-float {
        0%, 100% { 
          transform: translateY(0) rotate(0deg);
        }
        50% { 
          transform: translateY(-8px) rotate(5deg);
        }
      }
      
      @keyframes sparkle {
        0%, 100% { 
          opacity: 0.3;
          transform: scale(1);
        }
        50% { 
          opacity: 1;
          transform: scale(1.5);
        }
      }
      
      .touring-guide-text span {
        animation: letter-float-once 2s ease-out forwards;
      }
      
      @keyframes letter-float-once {
        0% { 
          transform: translateY(20px) rotate(0deg);
          opacity: 0;
        }
        100% { 
          transform: translateY(0) rotate(0deg);
          opacity: 1;
        }
      }
      
      .animate-line-expand {
        animation: line-expand 1.5s ease-out forwards;
      }
      
      .animate-underline-expand {
        animation: underline-expand 1.2s ease-out 0.5s forwards;
      }
      
      .animate-pulse-glow {
        animation: pulse-glow 3s ease-in-out infinite;
      }
      
      .animate-line-expand-delayed {
        animation: line-expand-delayed 1.5s ease-out 1s forwards;
      }
      
      .animate-fade-in-up {
        animation: fade-in-up 1s ease-out forwards;
        opacity: 0;
      }
      
      .animate-icon-float {
        animation: icon-float 3s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }
}

export default TouringGuide;
